import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CellTowerRoundedIcon from '@mui/icons-material/CellTowerRounded';
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import NetworkCheckRoundedIcon from '@mui/icons-material/NetworkCheckRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import SensorsOffRoundedIcon from '@mui/icons-material/SensorsOffRounded';
import SettingsInputAntennaRoundedIcon from '@mui/icons-material/SettingsInputAntennaRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';

import { devicesApi } from '../api/devices';
import { gatewaysApi } from '../api/gateways';

const surfaceSx = {
  borderRadius: 3,
  border: '1px solid rgba(38, 65, 94, 0.12)',
  background: '#fff',
  boxShadow: 'none',
};

const tableHeadCellSx = {
  py: 0.9,
  px: 1.15,
  fontSize: 12,
  fontWeight: 850,
  color: '#526579',
  background: '#F7FAFE',
  borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
  whiteSpace: 'nowrap',
};

const tableCellSx = {
  py: 0.85,
  px: 1.15,
  fontSize: 13,
  borderColor: 'rgba(15, 23, 42, 0.06)',
  verticalAlign: 'middle',
};

const DEVICE_PAGE_SIZE = 25;
const GATEWAY_PAGE_SIZE = 20;
const COVERAGE_SYSTEM_TYPES = ['ХВС', 'ГВС', 'Отопление', 'Электросчетчик', 'УРТ'];

const floorIssueLabels = {
  no_data: 'Нет данных',
  low_device_coverage: 'Мало приборов на связи',
  single_gateway: 'Слышит одна БС',
  weak_signal: 'Слабый сигнал',
};

const recommendationLabels = {
  check_floor: 'Проверить приборы на месте',
  review_gateway_position: 'Проверить размещение БС',
  add_gateway: 'Рассмотреть дополнительную БС',
};

const installationPlaceLabels = {
  indoor: 'В помещении',
  facade: 'На фасаде',
  roof: 'На крыше',
  outdoor: 'На улице',
};

const moveVerdictLabels = {
  improved: 'Стало лучше',
  worse: 'Стало хуже',
  unchanged: 'Без заметных изменений',
  insufficient_data: 'Недостаточно данных',
};

function floorSortValue(value) {
  const match = String(value || '').match(/-?\d+/);
  return match ? Number(match[0]) : null;
}

function floorRangeLabel(item) {
  if (item.floor_from == null && item.floor_to == null) return 'этаж не указан';
  if (item.floor_from === item.floor_to) return `${item.floor_from} этаж`;
  return `${item.floor_from}-${item.floor_to} этажи`;
}

const healthMeta = {
  good: { label: 'Норма', color: 'success' },
  warning: { label: 'Нужна проверка', color: 'warning' },
  no_data: { label: 'Нет данных', color: 'default' },
  unknown: { label: 'Нет оценки', color: 'default' },
};

const reasonLabels = {
  'gateway not linked': 'Не привязана к LoRaWAN',
  'no uplinks in period': 'Нет связи',
  'repeated join without data': 'Join без данных',
  'weak downlink gateway': 'Неудачный выбор БС',
  'downlink without ack': 'Команда не подтверждена',
  'low ACK rate': 'Команды не подтверждаются',
  'duplicate fCnt': 'Дубли fCnt',
  'low gateway diversity': 'Мало БС',
  'low RSSI': 'Слабый RSSI',
  'low SNR': 'Слабый SNR',
};

const reasonActions = {
  'gateway not linked': {
    title: 'Указать Gateway ID',
    text: 'Откройте базовую станцию в разделе «Шлюзы» и укажите её LoRaWAN Gateway ID или MAC-адрес.',
    priority: 1,
  },
  'no uplinks in period': {
    title: 'Проверить питание и регистрацию',
    text: 'Нет рабочих пакетов за период. Проверьте питание прибора, DevEUI, привязку к клиенту и наличие пакетов в LoRaWAN-сервере.',
    priority: 2,
  },
  'repeated join without data': {
    title: 'Проверить ключи и профиль',
    text: 'Устройство пытается подключиться, но не начинает передачу данных. Проверьте AppKey/NwkKey, JoinEUI, профиль устройства и decoder.',
    priority: 2,
  },
  'weak downlink gateway': {
    title: 'Проверить маршрут ответа',
    text: 'Для ответа выбрана БС со слабым сигналом. Проверьте размещение БС, антенну и маршрут ответа.',
    priority: 3,
  },
  'downlink without ack': {
    title: 'Проверить передачу команд',
    text: 'Команда не подтверждена. Проверьте доступность БС, очередь команд и связь БС с сервером.',
    priority: 4,
  },
  'low ACK rate': {
    title: 'Проверить связь БС',
    text: 'Команды часто остаются без подтверждения. Проверьте связь БС с сервером.',
    priority: 5,
  },
  'duplicate fCnt': {
    title: 'Проверить повторы пакетов',
    text: 'Повторяются счётчики пакетов. Проверьте перезапуски устройства, session state и корректность обработки дублей.',
    priority: 6,
  },
  'low gateway diversity': {
    title: 'Проверить покрытие',
    text: 'Прибор слышит слишком мало БС. Проверьте зону установки, этаж, подвал/щитовую и необходимость дополнительной БС.',
    priority: 7,
  },
  'low RSSI': {
    title: 'Проверить уровень сигнала',
    text: 'Слабый RSSI. Проверьте антенну, расположение прибора, экранирование и расстояние до БС.',
    priority: 8,
  },
  'low SNR': {
    title: 'Проверить шум и помехи',
    text: 'Слабый SNR. Проверьте помехи, канал, SF, расположение антенны и качество радиосреды.',
    priority: 9,
  },
};

function formatDate(value) {
  if (!value) return 'Нет данных';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Нет данных';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatNumber(value, suffix = '') {
  if (value === null || value === undefined || value === '') return 'Нет данных';
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Нет данных';
  return `${number.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}${suffix}`;
}

function signedNumber(value, suffix = '') {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return 'Нет сравнения';
  const number = Number(value);
  return `${number > 0 ? '+' : ''}${formatNumber(number, suffix)}`;
}

function CoverageMetric({ label, value, note, tone = '#2E73B8' }) {
  return (
    <Box sx={{ minWidth: 0, px: 1.15, py: 1, borderRadius: 2.2, backgroundColor: `${tone}0B`, border: `1px solid ${tone}22` }}>
      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>{label}</Typography>
      <Typography sx={{ color: '#1F3550', fontSize: 19, lineHeight: 1.15, fontWeight: 950 }}>{value}</Typography>
      {note ? <Typography variant="caption" sx={{ color: '#64748B' }}>{note}</Typography> : null}
    </Box>
  );
}

function TimeProfile({ items }) {
  const maxPackets = Math.max(1, ...(items || []).map((item) => item.packets_count || 0));
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(24, minmax(18px, 1fr))', gap: 0.45, minWidth: 650, alignItems: 'end' }}>
      {(items || []).map((item) => {
        const height = Math.max(4, Math.round((item.packets_count / maxPackets) * 112));
        return (
          <Tooltip key={item.hour} arrow title={`${item.hour}:00 · ${item.packets_count} пакетов · ${item.devices_active} приборов`}>
            <Stack spacing={0.45} alignItems="center" justifyContent="flex-end">
              <Box sx={{ width: '100%', maxWidth: 24, height, borderRadius: '5px 5px 2px 2px', backgroundColor: item.packets_count ? '#2E73B8' : '#E5EAF0' }} />
              <Typography variant="caption" sx={{ color: '#64748B', fontSize: 9.5 }}>
                {String(item.hour).padStart(2, '0')}
              </Typography>
            </Stack>
          </Tooltip>
        );
      })}
    </Box>
  );
}

function DailyProfile({ items }) {
  return (
    <Box sx={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'minmax(24px, 1fr)', gap: 0.45, minWidth: Math.max(650, (items || []).length * 28), alignItems: 'end' }}>
      {(items || []).map((item) => {
        const height = Math.max(4, Math.round((item.coverage_percent / 100) * 112));
        const tone = item.coverage_percent >= 90 ? '#0E9F6E' : item.coverage_percent >= 60 ? '#D97706' : '#C2415D';
        return (
          <Tooltip key={item.day} arrow title={`${new Date(item.day).toLocaleDateString('ru-RU')} · ${item.coverage_percent}% · ${item.packets_count} пакетов`}>
            <Stack spacing={0.45} alignItems="center" justifyContent="flex-end">
              <Box sx={{ width: '100%', maxWidth: 24, height, borderRadius: '5px 5px 2px 2px', backgroundColor: tone }} />
              <Typography variant="caption" sx={{ color: '#64748B', fontSize: 9.5 }}>
                {new Date(item.day).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
              </Typography>
            </Stack>
          </Tooltip>
        );
      })}
    </Box>
  );
}

function StatCard({ icon, label, value, tone = '#2E73B8', muted, active = false, onClick }) {
  return (
    <Paper
      component={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      variant="outlined"
      onClick={onClick}
      sx={{
        ...surfaceSx,
        position: 'relative',
        height: '100%',
        minHeight: 86,
        p: 1.2,
        overflow: 'hidden',
        textAlign: 'left',
        cursor: onClick ? 'pointer' : 'default',
        background: `linear-gradient(135deg, #FFFFFF 0%, ${tone}0F 100%)`,
        borderColor: active ? `${tone}88` : surfaceSx.border,
        transition: 'border-color 160ms ease, transform 160ms ease, background-color 160ms ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 12,
          bottom: 12,
          width: 3,
          borderRadius: '0 999px 999px 0',
          backgroundColor: tone,
          opacity: active ? 1 : 0.75,
        },
        '&:hover': onClick ? {
          transform: 'translateY(-1px)',
          borderColor: `${tone}55`,
        } : undefined,
        '&:focus-visible': {
          outline: `2px solid ${tone}55`,
          outlineOffset: 2,
        },
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center" sx={{ height: '100%' }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            color: tone,
            backgroundColor: '#FFFFFF',
            border: `1px solid ${tone}2E`,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 850, lineHeight: 1.15 }}>
            {label}
          </Typography>
          <Typography sx={{ color: '#1F3550', fontWeight: 950, fontSize: 22, lineHeight: 1.05, mt: 0.15 }}>
            {value}
          </Typography>
          {muted ? (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: '#64748B', fontWeight: 650, lineHeight: 1.2, mt: 0.25 }}
            >
              {muted}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function HealthChip({ value }) {
  const meta = healthMeta[value] || { label: value || 'Нет данных', color: 'default' };
  return <Chip size="small" label={meta.label} color={meta.color} variant="outlined" sx={{ fontWeight: 800 }} />;
}

function GatewayBindingChip({ gateway }) {
  const meta = {
    linked: { label: 'Привязана', color: 'success' },
    topic_only: { label: 'ID из топика', color: 'warning' },
    missing: { label: 'Не настроена', color: 'error' },
    traffic_only: { label: 'Нет записи шлюза', color: 'warning' },
  }[gateway?.configuration_status] || { label: 'Не настроена', color: 'default' };
  return <Chip size="small" label={meta.label} color={meta.color} variant="outlined" sx={{ fontWeight: 800 }} />;
}

function ReasonChips({ reasons }) {
  const items = Array.isArray(reasons) ? reasons.filter(Boolean) : [];
  if (!items.length) {
    return <Typography variant="caption" sx={{ color: '#94A3B8' }}>Нет данных</Typography>;
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {items.slice(0, 2).map((reason) => (
        <Chip
          key={reason}
          size="small"
          label={reasonLabels[reason] || reason}
          variant="outlined"
          sx={{
            height: 22,
            borderColor: 'rgba(245, 158, 11, 0.35)',
            color: '#9A5B00',
            backgroundColor: 'rgba(245, 158, 11, 0.08)',
            fontSize: 11,
            fontWeight: 800,
          }}
        />
      ))}
      {items.length > 2 ? (
        <Chip size="small" label={`+${items.length - 2}`} variant="outlined" sx={{ height: 22, fontSize: 11 }} />
      ) : null}
    </Stack>
  );
}

function getPrimaryAction(reasons) {
  const items = Array.isArray(reasons) ? reasons.filter((reason) => reasonActions[reason]) : [];
  if (!items.length) return null;
  return items
    .map((reason) => ({ reason, ...reasonActions[reason] }))
    .sort((a, b) => a.priority - b.priority)[0];
}

function ActionHint({ reasons }) {
  const action = getPrimaryAction(reasons);
  if (!action) {
    return <Typography variant="caption" sx={{ color: '#94A3B8' }}>Нет данных</Typography>;
  }
  return (
    <Tooltip title={action.text} arrow placement="top">
      <Chip
        size="small"
        label={action.title}
        variant="outlined"
        sx={{
          maxWidth: 190,
          height: 24,
          justifyContent: 'flex-start',
          borderColor: 'rgba(46, 115, 184, 0.28)',
          color: '#1F5F99',
          backgroundColor: 'rgba(46, 115, 184, 0.07)',
          fontSize: 11,
          fontWeight: 850,
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
      />
    </Tooltip>
  );
}

export default function LoRaWanDiagnosticsPage() {
  const navigate = useNavigate();
  const floorCoverageCacheRef = useRef(new Map());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = window.localStorage.getItem('sidebar-collapsed');
      return saved === null ? true : saved === '1';
    } catch {
      return true;
    }
  });
  const [overview, setOverview] = useState(null);
  const [gateways, setGateways] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [coverageBuildings, setCoverageBuildings] = useState([]);
  const [coverageBuildingsLoading, setCoverageBuildingsLoading] = useState(true);
  const [coverageBuildingId, setCoverageBuildingId] = useState('');
  const [coverageDays, setCoverageDays] = useState(30);
  const [coverageSystemType, setCoverageSystemType] = useState('');
  const [coverageView, setCoverageView] = useState('floors');
  const [floorCoverage, setFloorCoverage] = useState(null);
  const [floorCoverageLoading, setFloorCoverageLoading] = useState(false);
  const [floorCoverageError, setFloorCoverageError] = useState('');
  const [coverageRefreshKey, setCoverageRefreshKey] = useState(0);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [gatewayDetail, setGatewayDetail] = useState(null);
  const [gatewayDetailLoading, setGatewayDetailLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [gatewayFilter, setGatewayFilter] = useState('all');
  const [devicePage, setDevicePage] = useState(1);
  const [gatewayPage, setGatewayPage] = useState(1);
  const [error, setError] = useState('');

  const loadOverview = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setRefreshing(Boolean(silent));
    setError('');
    try {
      const [diagnosticsResult, trafficResult] = await Promise.allSettled([
        Promise.all([
          devicesApi.getLoRaWanDiagnosticsOverview(5000, true),
          devicesApi.listLoRaWanDiagnosticGateways(500),
        ]),
        devicesApi.getLoRaWanTrafficOverview(24, 100),
      ]);
      if (diagnosticsResult.status === 'rejected') {
        throw diagnosticsResult.reason;
      }
      const [data, gatewayData] = diagnosticsResult.value;
      setOverview(data);
      setGateways(Array.isArray(gatewayData) ? gatewayData : []);
      setTraffic(trafficResult.status === 'fulfilled' ? trafficResult.value : null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось загрузить диагностику LoRaWAN.');
      if (!silent) {
        setOverview(null);
        setGateways([]);
        setTraffic(null);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadCoverageBuildings = useCallback(async () => {
    setCoverageBuildingsLoading(true);
    try {
      const data = await gatewaysApi.listLoRaWanBuildingSummary();
      const buildings = Array.isArray(data) ? data : [];
      setCoverageBuildings(buildings);
      setCoverageBuildingId((current) => (
        buildings.some((item) => item.building_id === current)
          ? current
          : buildings[0]?.building_id || ''
      ));
    } catch {
      setCoverageBuildings([]);
      setCoverageBuildingId('');
    } finally {
      setCoverageBuildingsLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(() => {
    floorCoverageCacheRef.current.clear();
    setCoverageRefreshKey((current) => current + 1);
    loadCoverageBuildings();
    loadOverview({ silent: true });
  }, [loadCoverageBuildings, loadOverview]);

  const openGatewayDetail = useCallback(async (gateway) => {
    if (!gateway?.gateway_id) return;
    setSelectedGateway(gateway);
    setGatewayDetail(null);
    setGatewayDetailLoading(true);
    try {
      const data = await devicesApi.getLoRaWanGatewayDiagnostics(gateway.gateway_id, 30);
      setGatewayDetail(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось загрузить базовую станцию.');
    } finally {
      setGatewayDetailLoading(false);
    }
  }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setError('');
    try {
      const blob = await devicesApi.exportLoRaWanDiagnostics();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lorawan_diagnostics_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось выгрузить отчёт.');
    } finally {
      setExporting(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadCoverageBuildings();
  }, [loadCoverageBuildings]);

  useEffect(() => {
    const syncSidebarState = (event) => {
      if (typeof event?.detail?.collapsed === 'boolean') {
        setSidebarCollapsed(event.detail.collapsed);
        return;
      }
      try {
        const saved = window.localStorage.getItem('sidebar-collapsed');
        setSidebarCollapsed(saved === null ? true : saved === '1');
      } catch {
        setSidebarCollapsed(true);
      }
    };
    window.addEventListener('sidebar-state-changed', syncSidebarState);
    window.addEventListener('storage', syncSidebarState);
    return () => {
      window.removeEventListener('sidebar-state-changed', syncSidebarState);
      window.removeEventListener('storage', syncSidebarState);
    };
  }, []);

  useEffect(() => {
    if (!coverageBuildings.length) {
      setCoverageBuildingId('');
      return;
    }
    if (!coverageBuildings.some((item) => item.building_id === coverageBuildingId)) {
      setCoverageBuildingId(coverageBuildings[0].building_id);
    }
  }, [coverageBuildingId, coverageBuildings]);

  useEffect(() => {
    if (!coverageBuildingId) {
      setFloorCoverage(null);
      return undefined;
    }
    const controller = new AbortController();
    const cacheKey = `${coverageBuildingId}:${coverageDays}:${coverageSystemType || 'all'}`;
    const cached = floorCoverageCacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.savedAt < 5 * 60 * 1000) {
      setFloorCoverage(cached.data);
      setFloorCoverageError('');
      setFloorCoverageLoading(false);
      return () => controller.abort();
    }
    let cancelled = false;
    const loadFloorCoverage = async () => {
      setFloorCoverageLoading(true);
      setFloorCoverageError('');
      try {
        const data = await gatewaysApi.getLoRaWanFloorCoverage(coverageBuildingId, {
          days: coverageDays,
          system_type: coverageSystemType || undefined,
        }, controller.signal);
        if (!cancelled) {
          floorCoverageCacheRef.current.set(cacheKey, { data, savedAt: Date.now() });
          if (floorCoverageCacheRef.current.size > 12) {
            const oldestKey = floorCoverageCacheRef.current.keys().next().value;
            floorCoverageCacheRef.current.delete(oldestKey);
          }
          setFloorCoverage(data);
        }
      } catch (err) {
        if (!cancelled && err?.code !== 'ERR_CANCELED') {
          setFloorCoverage(null);
          setFloorCoverageError(err?.response?.data?.detail || 'Не удалось загрузить покрытие по этажам.');
        }
      } finally {
        if (!cancelled) setFloorCoverageLoading(false);
      }
    };
    loadFloorCoverage();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [coverageBuildingId, coverageDays, coverageRefreshKey, coverageSystemType]);

  useEffect(() => {
    setDevicePage(1);
  }, [deviceFilter, overview?.latest_period_end]);

  useEffect(() => {
    setGatewayPage(1);
  }, [gatewayFilter, overview?.latest_period_end]);

  const problemCount = useMemo(
    () =>
      (overview?.devices_warning || 0) +
      (overview?.devices_no_data || 0) +
      gateways.filter((item) => item.health_status !== 'good' || item.configuration_status !== 'linked').length +
      (traffic?.unknown_devices_total || 0),
    [gateways, overview, traffic]
  );

  const filteredProblemDevices = useMemo(() => {
    const items = overview?.problem_devices || [];
    if (deviceFilter === 'warning') {
      return items.filter((item) => item.health_status === 'warning');
    }
    if (deviceFilter === 'no_data') {
      return items.filter((item) => item.health_status === 'no_data');
    }
    return items;
  }, [deviceFilter, overview]);

  const filteredGateways = useMemo(() => {
    const items = gateways || [];
    if (gatewayFilter === 'warning') {
      return items.filter((item) => item.health_status !== 'good' || item.configuration_status !== 'linked');
    }
    return items;
  }, [gatewayFilter, gateways]);

  const devicePageCount = Math.max(1, Math.ceil(filteredProblemDevices.length / DEVICE_PAGE_SIZE));
  const gatewayPageCount = Math.max(1, Math.ceil(filteredGateways.length / GATEWAY_PAGE_SIZE));
  const safeDevicePage = Math.min(devicePage, devicePageCount);
  const safeGatewayPage = Math.min(gatewayPage, gatewayPageCount);
  const paginatedDevices = useMemo(() => {
    const start = (safeDevicePage - 1) * DEVICE_PAGE_SIZE;
    return filteredProblemDevices.slice(start, start + DEVICE_PAGE_SIZE);
  }, [filteredProblemDevices, safeDevicePage]);
  const paginatedGateways = useMemo(() => {
    const start = (safeGatewayPage - 1) * GATEWAY_PAGE_SIZE;
    return filteredGateways.slice(start, start + GATEWAY_PAGE_SIZE);
  }, [filteredGateways, safeGatewayPage]);
  const deviceRangeStart = filteredProblemDevices.length ? (safeDevicePage - 1) * DEVICE_PAGE_SIZE + 1 : 0;
  const deviceRangeEnd = Math.min(safeDevicePage * DEVICE_PAGE_SIZE, filteredProblemDevices.length);
  const gatewayRangeStart = filteredGateways.length ? (safeGatewayPage - 1) * GATEWAY_PAGE_SIZE + 1 : 0;
  const gatewayRangeEnd = Math.min(safeGatewayPage * GATEWAY_PAGE_SIZE, filteredGateways.length);

  const floorCoverageLayout = useMemo(() => {
    const columns = new Map();
    const floorLabels = new Set();
    (floorCoverage?.floors || []).forEach((item) => {
      const key = `${item.corp_id}:${item.section_number || ''}`;
      if (!columns.has(key)) {
        columns.set(key, {
          key,
          title: [item.corp_name, item.section_number ? `подъезд ${item.section_number}` : null]
            .filter(Boolean)
            .join(' · ') || 'Дом',
          floors: new Map(),
        });
      }
      const floorLabel = item.floor_number || 'Не указан';
      columns.get(key).floors.set(floorLabel, item);
      floorLabels.add(floorLabel);
    });
    const floors = Array.from(floorLabels).sort((left, right) => {
      const leftNumber = floorSortValue(left);
      const rightNumber = floorSortValue(right);
      if (leftNumber != null && rightNumber != null) return rightNumber - leftNumber;
      if (leftNumber != null) return -1;
      if (rightNumber != null) return 1;
      return String(left).localeCompare(String(right), 'ru');
    });
    return { columns: Array.from(columns.values()), floors };
  }, [floorCoverage]);

  const topActions = useMemo(() => {
    const counts = new Map();
    [...(overview?.problem_devices || []), ...gateways].forEach((item) => {
      (item.reasons || []).forEach((reason) => {
        if (!reasonActions[reason]) return;
        counts.set(reason, (counts.get(reason) || 0) + 1);
      });
    });
    return Array.from(counts.entries())
      .map(([reason, count]) => ({ reason, count, ...reasonActions[reason] }))
      .sort((a, b) => a.priority - b.priority || b.count - a.count)
      .slice(0, 3);
  }, [gateways, overview]);

  const deviceListTitle = deviceFilter === 'warning'
    ? 'Приборы, которым нужна проверка'
    : deviceFilter === 'no_data'
      ? 'Приборы без данных'
      : 'Все приборы';
  const gatewayListTitle = gatewayFilter === 'warning' ? 'Базовые станции с замечаниями' : 'Базовые станции';

  if (loading) {
    return (
      <Box sx={{ minHeight: '55vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <Box className="home-container page-animate" sx={{ px: { xs: 2, md: 3 }, py: 2.5, width: '100%' }}>
      <Stack spacing={1.5}>
        {error ? <Alert severity="warning">{error}</Alert> : null}

        <Paper
          variant="outlined"
          sx={{
            ...surfaceSx,
            p: 1.25,
            background: '#FFFFFF',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.25}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  color: '#2E73B8',
                  background: 'rgba(46, 115, 184, 0.08)',
                  border: '1px solid rgba(46, 115, 184, 0.16)',
                }}
              >
                <SettingsInputAntennaRoundedIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: '#1F3550', fontWeight: 900, lineHeight: 1.15 }}>
                  Диагностика LoRaWAN
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 650, mt: 0.15 }}>
                  Последний расчёт: {formatDate(overview?.latest_period_end)}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={problemCount ? `Проблем: ${problemCount}` : 'Без критичных замечаний'}
                color={problemCount ? 'warning' : 'success'}
                variant="outlined"
                sx={{ fontWeight: 850 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshRoundedIcon />}
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 850 }}
              >
                Обновить
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<FileDownloadRoundedIcon />}
                onClick={handleExport}
                disabled={exporting || !overview?.latest_period_end}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 850, boxShadow: 'none' }}
              >
                CSV
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {!overview?.latest_period_end && !traffic?.packets_total ? (
          <Alert severity="info">
            Данные диагностики пока недоступны.
          </Alert>
        ) : null}

        {topActions.length ? (
          <Paper variant="outlined" sx={{ ...surfaceSx, p: 1.25 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Typography sx={{ color: '#1F3550', fontWeight: 900, minWidth: 150 }}>
                Что проверить
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                {topActions.map((action) => (
                  <Tooltip key={action.reason} title={action.text} arrow placement="top">
                    <Chip
                      size="small"
                      label={`${action.title}: ${action.count}`}
                      variant="outlined"
                      sx={{
                        height: 26,
                        borderColor: 'rgba(46, 115, 184, 0.28)',
                        color: '#1F5F99',
                        backgroundColor: 'rgba(46, 115, 184, 0.07)',
                        fontWeight: 850,
                      }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Stack>
          </Paper>
        ) : null}

        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<SensorsRoundedIcon fontSize="small" />}
              label="Приборы"
              value={overview?.devices_total || 0}
              muted={`Норма: ${overview?.devices_good || 0}`}
              active={deviceFilter === 'all'}
              onClick={() => setDeviceFilter('all')}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<NetworkCheckRoundedIcon fontSize="small" />}
              label="Нужна проверка"
              value={overview?.devices_warning || 0}
              tone="#F97316"
              active={deviceFilter === 'warning'}
              onClick={() => setDeviceFilter('warning')}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<SensorsOffRoundedIcon fontSize="small" />}
              label="Нет данных"
              value={overview?.devices_no_data || 0}
              tone="#DC2626"
              active={deviceFilter === 'no_data'}
              onClick={() => setDeviceFilter('no_data')}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<CellTowerRoundedIcon fontSize="small" />}
              label="Базовые станции"
              value={gateways.length}
              muted={`Проверить: ${gateways.filter((item) => item.health_status !== 'good' || item.configuration_status !== 'linked').length}`}
              active={gatewayFilter === 'warning'}
              onClick={() => setGatewayFilter((current) => (current === 'warning' ? 'all' : 'warning'))}
            />
          </Grid>
        </Grid>

        {traffic ? (
          <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
              spacing={1}
              sx={{ px: 1.4, py: 1.1 }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ color: '#1F3550', fontWeight: 900 }}>
                  Эфир базовых станций
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  Последние 24 часа
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Пакетов: ${traffic.packets_total}`} variant="outlined" />
                <Chip size="small" label={`Распознано: ${traffic.known_packets}`} color="success" variant="outlined" />
                <Chip size="small" label={`Не распознано: ${traffic.unknown_packets}`} color={traffic.unknown_packets ? 'warning' : 'default'} variant="outlined" />
                <Chip size="small" label={`Приёмов БС: ${traffic.receptions_total}`} variant="outlined" />
                <Chip size="small" label={`Активных БС: ${traffic.gateways_active}`} variant="outlined" />
                <Chip
                  size="small"
                  label={`Неизвестных устройств: ${traffic.unknown_devices_total}`}
                  color={traffic.unknown_devices_total ? 'warning' : 'success'}
                  variant="outlined"
                  sx={{ fontWeight: 800 }}
                />
              </Stack>
            </Stack>
            <Divider />
            {traffic.unknown_devices.length ? (
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader sx={{ minWidth: 940 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>Неизвестный DevEUI</TableCell>
                      <TableCell sx={tableHeadCellSx}>Пакетов</TableCell>
                      <TableCell sx={tableHeadCellSx}>Базовые станции</TableCell>
                      <TableCell sx={tableHeadCellSx}>Лучший RSSI/SNR</TableCell>
                      <TableCell sx={tableHeadCellSx}>Первый пакет</TableCell>
                      <TableCell sx={tableHeadCellSx}>Последний пакет</TableCell>
                      <TableCell sx={tableHeadCellSx}>Что проверить</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {traffic.unknown_devices.map((item) => (
                      <TableRow key={item.dev_eui} hover>
                        <TableCell sx={tableCellSx}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <HelpOutlineRoundedIcon sx={{ color: '#D97706', fontSize: 18 }} />
                            <Typography sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 850 }}>
                              {item.dev_eui}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={tableCellSx}>{item.packets_count}</TableCell>
                        <TableCell sx={tableCellSx}>
                          <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{item.gateway_count}</Typography>
                          <Typography variant="caption" sx={{ color: '#64748B', fontFamily: 'monospace' }}>
                            {(item.gateway_ids || []).slice(0, 2).join(', ')}
                            {item.gateway_ids?.length > 2 ? ` +${item.gateway_ids.length - 2}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableCellSx}>
                          {formatNumber(item.best_rssi, ' dBm')} / {formatNumber(item.best_snr, ' dB')}
                        </TableCell>
                        <TableCell sx={tableCellSx}>{formatDate(item.first_seen_at)}</TableCell>
                        <TableCell sx={tableCellSx}>{formatDate(item.last_seen_at)}</TableCell>
                        <TableCell sx={tableCellSx}>
                          <Tooltip title="Проверьте, заведён ли прибор в системе, корректны ли DevEUI, ключи активации и профиль устройства." arrow>
                            <Chip size="small" label="Проверить регистрацию" color="warning" variant="outlined" />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="success" variant="outlined" sx={{ m: 1.25 }}>
                Неизвестные устройства не обнаружены.
              </Alert>
            )}
            {traffic.unidentified_packets > 0 ? (
              <Box sx={{ px: 1.4, py: 1, borderTop: '1px solid rgba(15, 23, 42, 0.06)' }}>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  Не удалось определить DevEUI у {traffic.unidentified_packets} пакетов. Они учтены в общей активности БС.
                </Typography>
              </Box>
            ) : null}
          </Paper>
        ) : null}

        <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
          <Stack
            spacing={1}
            sx={{ px: 1.4, py: 1.1 }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={0.8}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="subtitle1" sx={{ color: '#1F3550', fontWeight: 900 }}>
                Покрытие LoRaWAN
              </Typography>
              <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap>
                {floorCoverage ? (
                  <>
                    <Chip size="small" variant="outlined" label={`Приборы: ${floorCoverage.devices_active}/${floorCoverage.devices_total}`} />
                    <Chip
                      size="small"
                      variant="outlined"
                      color={floorCoverage.floors_problem ? 'warning' : 'success'}
                      label={`Проблемные этажи: ${floorCoverage.floors_problem}`}
                    />
                  </>
                ) : null}
              </Stack>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: sidebarCollapsed
                    ? 'minmax(260px, 1fr) 130px 170px auto'
                    : 'minmax(260px, 1fr) 130px 170px',
                  xl: 'minmax(300px, 1fr) 130px 170px auto',
                },
                gap: 0.8,
                alignItems: 'center',
              }}
            >
              <TextField
                select
                size="small"
                label="Дом"
                value={coverageBuildingId}
                onChange={(event) => setCoverageBuildingId(event.target.value)}
                disabled={!coverageBuildings.length}
                sx={{ minWidth: 0 }}
              >
                {coverageBuildings.map((item) => (
                  <MenuItem key={item.building_id} value={item.building_id}>
                    {item.building_name || item.building_id}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Период"
                value={coverageDays}
                onChange={(event) => setCoverageDays(Number(event.target.value))}
                sx={{ minWidth: 0 }}
              >
                <MenuItem value={7}>7 дней</MenuItem>
                <MenuItem value={30}>30 дней</MenuItem>
                <MenuItem value={90}>90 дней</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Тип прибора"
                value={coverageSystemType}
                onChange={(event) => setCoverageSystemType(event.target.value)}
                sx={{ minWidth: 0 }}
              >
                <MenuItem value="">Все типы</MenuItem>
                {COVERAGE_SYSTEM_TYPES.map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </TextField>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={coverageView}
                onChange={(_event, value) => value && setCoverageView(value)}
                sx={{
                  gridColumn: {
                    xs: '1 / -1',
                    sm: '1 / -1',
                    lg: sidebarCollapsed ? 'auto' : '1 / -1',
                    xl: 'auto',
                  },
                  justifySelf: {
                    xs: 'stretch',
                    sm: 'end',
                  },
                  p: 0.35,
                  borderRadius: 2.2,
                  backgroundColor: '#F3F6FA',
                  '& .MuiToggleButton-root': {
                    flex: { xs: 1, sm: 'initial' },
                    border: 0,
                    borderRadius: '7px !important',
                    px: 1.1,
                    py: 0.55,
                    textTransform: 'none',
                    fontWeight: 800,
                    color: '#526579',
                  },
                  '& .Mui-selected': { backgroundColor: '#FFFFFF !important', color: '#1F5F99', boxShadow: '0 1px 4px rgba(31, 53, 80, 0.12)' },
                }}
              >
                <ToggleButton value="floors"><ApartmentRoundedIcon sx={{ fontSize: 17, mr: 0.55 }} />Этажи</ToggleButton>
                <ToggleButton value="gateways"><CellTowerRoundedIcon sx={{ fontSize: 17, mr: 0.55 }} />БС</ToggleButton>
                <ToggleButton value="time"><AccessTimeRoundedIcon sx={{ fontSize: 17, mr: 0.55 }} />Динамика</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>
          <Divider />

          {coverageBuildingsLoading && !coverageBuildings.length ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
          ) : !coverageBuildings.length ? (
            <Box sx={{ px: 1.4, py: 2.5, color: '#64748B' }}>
              <Typography variant="body2">Домов с LoRaWAN-приборами нет.</Typography>
            </Box>
          ) : floorCoverageLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
          ) : floorCoverageError ? (
            <Alert severity="warning" sx={{ m: 1.25 }}>{floorCoverageError}</Alert>
          ) : floorCoverage?.floors?.length ? (
            <Stack spacing={1.25} sx={{ p: 1.25 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' }, gap: 0.8 }}>
                <CoverageMetric
                  label="Приборы на связи"
                  value={`${floorCoverage.current?.coverage_percent || 0}%`}
                  note={signedNumber(floorCoverage.comparison?.coverage_delta, ' п.п.')}
                  tone="#0E9F6E"
                />
                <CoverageMetric
                  label="Пакеты"
                  value={formatNumber(floorCoverage.current?.packets_count)}
                  note={signedNumber(floorCoverage.comparison?.packets_delta_percent, '%')}
                />
                <CoverageMetric
                  label="Слабый RSSI"
                  value={formatNumber(floorCoverage.current?.rssi_distribution?.p10, ' dBm')}
                  note={signedNumber(floorCoverage.comparison?.rssi_p10_delta, ' dB')}
                  tone="#D97706"
                />
                <CoverageMetric
                  label="Слабый SNR"
                  value={formatNumber(floorCoverage.current?.snr_distribution?.p10, ' dB')}
                  note={signedNumber(floorCoverage.comparison?.snr_p10_delta, ' dB')}
                  tone="#D97706"
                />
                <CoverageMetric
                  label="Потеря пакетов"
                  value={formatNumber(floorCoverage.current?.fcnt_missing)}
                  note={`Повторные: ${floorCoverage.current?.fcnt_duplicates || 0}`}
                  tone={(floorCoverage.current?.fcnt_missing || 0) > 0 ? '#C2415D' : '#0E9F6E'}
                />
                <CoverageMetric
                  label="Доставка команд"
                  value={floorCoverage.current?.ack_rate == null ? 'Нет команд' : `${floorCoverage.current.ack_rate}%`}
                  note={floorCoverage.current?.ack_rate == null ? null : `Доставлено: ${floorCoverage.current?.ack_count || 0}/${floorCoverage.current?.downlink_count || 0}`}
                  tone="#7456A4"
                />
              </Box>

              <Stack direction="row" spacing={0.7} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>Полнота данных</Typography>
                <Box sx={{ width: 120, height: 7, borderRadius: 99, overflow: 'hidden', backgroundColor: '#E8EDF3' }}>
                  <Box sx={{ width: `${floorCoverage.confidence_percent || 0}%`, height: '100%', borderRadius: 99, backgroundColor: (floorCoverage.confidence_percent || 0) >= 70 ? '#0E9F6E' : '#D97706' }} />
                </Box>
                <Typography variant="caption" sx={{ color: '#1F3550', fontWeight: 900 }}>{floorCoverage.confidence_percent || 0}%</Typography>
                <Typography variant="caption" sx={{ color: '#64748B' }}>Сравнение с предыдущими {coverageDays} днями</Typography>
              </Stack>

              {coverageView === 'floors' ? <>
              <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.max(1, floorCoverageLayout.columns.length)}, minmax(190px, 1fr))`,
                    gap: 1,
                    minWidth: floorCoverageLayout.columns.length > 3 ? floorCoverageLayout.columns.length * 200 : '100%',
                  }}
                >
                  {floorCoverageLayout.columns.map((column) => (
                    <Stack key={column.key} spacing={0.65}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#526579', fontWeight: 900, px: 0.5, minHeight: 20 }}
                      >
                        {column.title}
                      </Typography>
                      {floorCoverageLayout.floors.map((floorLabel) => {
                        const item = column.floors.get(floorLabel);
                        if (!item) {
                          return <Box key={floorLabel} sx={{ minHeight: 72, borderRadius: 2, border: '1px dashed rgba(100, 116, 139, 0.16)' }} />;
                        }
                        const palette = item.status === 'critical'
                          ? { bg: '#FFF1F2', border: '#FDA4AF', text: '#BE123C' }
                          : item.status === 'warning'
                            ? { bg: '#FFF8E8', border: '#F6C768', text: '#9A5B00' }
                            : { bg: '#ECFDF5', border: '#86DDB7', text: '#047857' };
                        return (
                          <Tooltip
                            key={floorLabel}
                            arrow
                            placement="top"
                            title={(
                              <Box>
                                <div>Приборы: {item.devices_active}/{item.devices_total}</div>
                                <div>БС: {item.gateways_count}</div>
                                <div>RSSI/SNR: {formatNumber(item.rssi_avg, ' dBm')} / {formatNumber(item.snr_avg, ' dB')}</div>
                                {(item.issue_codes || []).map((code) => <div key={code}>{floorIssueLabels[code] || code}</div>)}
                              </Box>
                            )}
                          >
                            <Box
                              sx={{
                                minHeight: 72,
                                borderRadius: 2,
                                border: `1px solid ${palette.border}`,
                                backgroundColor: palette.bg,
                                px: 1,
                                py: 0.75,
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                                <Typography sx={{ color: '#1F3550', fontWeight: 900, fontSize: 13 }}>
                                  {floorLabel === 'Не указан' ? floorLabel : `${floorLabel} этаж`}
                                </Typography>
                                <Typography sx={{ color: palette.text, fontWeight: 900, fontSize: 14 }}>
                                  {item.coverage_percent}%
                                </Typography>
                              </Stack>
                              <Typography variant="caption" sx={{ color: '#526579' }}>
                                {item.devices_active}/{item.devices_total} приборов · {item.gateways_count} БС
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ color: palette.text, fontWeight: 750 }}>
                                {item.issue_codes?.length ? floorIssueLabels[item.issue_codes[0]] : 'Норма'}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  ))}
                </Box>
              </Box>

              {floorCoverage.recommendations?.length ? (
                <Stack spacing={0.65}>
                  <Typography variant="subtitle2" sx={{ color: '#1F3550', fontWeight: 900 }}>
                    Зоны для проверки
                  </Typography>
                  {floorCoverage.recommendations.map((item, index) => (
                    <Stack
                      key={`${item.corp_id}:${item.section_number || ''}:${item.code}:${index}`}
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={0.75}
                      alignItems={{ xs: 'flex-start', md: 'center' }}
                      sx={{ px: 1, py: 0.8, borderRadius: 2, backgroundColor: item.severity === 'critical' ? '#FFF1F2' : '#FFF8E8' }}
                    >
                      <Typography sx={{ color: '#1F3550', fontSize: 13, fontWeight: 850 }}>
                        {[item.corp_name, item.section_number ? `подъезд ${item.section_number}` : null, floorRangeLabel(item)]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                      <Chip size="small" variant="outlined" label={floorIssueLabels[item.code] || item.code} />
                      <Chip size="small" variant="outlined" label={`Приборов: ${item.devices_affected}`} />
                      <Typography variant="body2" sx={{ color: '#526579', fontWeight: 700 }}>
                        {recommendationLabels[item.action] || item.action}
                        {item.suggested_floor != null && item.action !== 'check_floor' ? `, ориентир ${item.suggested_floor} этаж` : ''}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : null}

              </> : null}

              {coverageView === 'gateways' && floorCoverage.gateway_ranges?.length ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 1 }}>
                    {floorCoverage.gateway_ranges.map((item) => (
                      <Paper
                        key={item.gateway_id}
                        variant="outlined"
                        sx={{ borderRadius: 2.4, p: 1.2, borderColor: item.gateway_choice_problem_count ? '#F2B8C4' : 'rgba(38, 65, 94, 0.12)', boxShadow: 'none' }}
                      >
                        <Stack spacing={0.9}>
                          <Stack direction="row" spacing={0.8} alignItems="center">
                            <Box sx={{ width: 32, height: 32, borderRadius: 1.8, display: 'grid', placeItems: 'center', color: '#1F5F99', backgroundColor: '#EDF5FC' }}>
                              <CellTowerRoundedIcon fontSize="small" />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography noWrap sx={{ color: '#1F3550', fontWeight: 900, fontSize: 14 }}>{item.gateway_name || item.gateway_id}</Typography>
                              <Typography variant="caption" sx={{ color: '#64748B' }}>{item.floor_from ?? '?'}-{item.floor_to ?? '?'} этажи · {item.floors_count} зон</Typography>
                            </Box>
                            {item.unique_devices_count ? <Chip size="small" color="warning" variant="outlined" label={`Только здесь: ${item.unique_devices_count}`} /> : null}
                          </Stack>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.65 }}>
                            <CoverageMetric label="Приборы" value={item.devices_heard} />
                            <CoverageMetric label="Слабый RSSI" value={formatNumber(item.rssi_distribution?.p10, ' dBm')} tone="#D97706" />
                            <CoverageMetric label="Слабый SNR" value={formatNumber(item.snr_distribution?.p10, ' dB')} tone="#D97706" />
                          </Box>
                          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                            <Chip size="small" variant="outlined" label={`Пакеты: ${item.packets_count}`} />
                            <Chip size="small" variant="outlined" label={item.ack_rate == null ? 'Команд нет' : `Доставка команд: ${item.ack_rate}%`} />
                            {item.gateway_choice_problem_count ? <Chip size="small" color="error" variant="outlined" label={`Неудачный выбор БС: ${item.gateway_choice_problem_count}`} /> : null}
                          </Stack>
                          {(item.installation_section || item.installation_floor || item.antenna_type) ? (
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                              {[
                                item.installation_section ? `секция ${item.installation_section}` : null,
                                item.installation_floor ? `${item.installation_floor} этаж` : null,
                                item.installation_place ? installationPlaceLabels[item.installation_place] || item.installation_place : null,
                                item.antenna_type,
                                item.antenna_height_m != null ? `${item.antenna_height_m} м` : null,
                                item.antenna_gain_dbi != null ? `${item.antenna_gain_dbi} dBi` : null,
                              ].filter(Boolean).join(' · ')}
                            </Typography>
                          ) : null}
                          {item.move_comparison ? (
                            <Box sx={{ px: 0.9, py: 0.7, borderRadius: 1.8, backgroundColor: item.move_comparison.verdict === 'improved' ? '#ECFDF5' : item.move_comparison.verdict === 'worse' ? '#FFF1F2' : '#F5F7FA' }}>
                              <Typography variant="caption" sx={{ color: '#1F3550', fontWeight: 850 }}>
                                После перемещения: {moveVerdictLabels[item.move_comparison.verdict] || item.move_comparison.verdict}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ color: '#64748B' }}>
                                Приборы: было {item.move_comparison.before_devices}, стало {item.move_comparison.after_devices}. Слабый RSSI: было {formatNumber(item.move_comparison.before_rssi_p10)}, стало {formatNumber(item.move_comparison.after_rssi_p10)}
                              </Typography>
                            </Box>
                          ) : null}
                        </Stack>
                      </Paper>
                    ))}
                </Box>
              ) : null}

              {coverageView === 'gateways' && !floorCoverage.gateway_ranges?.length ? (
                <Typography variant="body2" sx={{ color: '#64748B', py: 1 }}>За выбранный период базовые станции не слышали приборы дома.</Typography>
              ) : null}

              {coverageView === 'time' ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ color: '#1F3550', fontWeight: 900 }}>Приборы на связи по дням</Typography>
                  <Box sx={{ overflowX: 'auto', pt: 0.5 }}><DailyProfile items={floorCoverage.daily_profile || []} /></Box>
                  <Typography variant="subtitle2" sx={{ color: '#1F3550', fontWeight: 900, pt: 0.5 }}>Пакеты по времени суток</Typography>
                  <Box sx={{ overflowX: 'auto', pt: 0.5 }}><TimeProfile items={floorCoverage.time_profile || []} /></Box>
                  <Typography variant="caption" sx={{ color: '#64748B' }}>Часы по UTC</Typography>
                </Stack>
              ) : null}
            </Stack>
          ) : (
            <Box sx={{ px: 1.4, py: 2.5, color: '#64748B' }}>
              <Typography variant="body2">На этажах нет LoRaWAN-приборов.</Typography>
            </Box>
          )}
        </Paper>

        <Grid container spacing={1.5}>
          <Grid item xs={12} lg={7}>
            <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.4, py: 1.1 }}>
                <Typography variant="subtitle1" sx={{ color: '#1F3550', fontWeight: 900 }}>
                  {deviceListTitle}
                </Typography>
                <Chip
                  size="small"
                  label={
                    deviceFilter === 'all'
                      ? `${filteredProblemDevices.length || 0} из ${overview?.devices_total || 0}`
                      : `${filteredProblemDevices.length || 0}`
                  }
                  variant="outlined"
                />
              </Stack>
              <Divider />
              <TableContainer sx={{ maxHeight: 460 }}>
                <Table size="small" stickyHeader sx={{ minWidth: 980 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>Прибор</TableCell>
                      <TableCell sx={tableHeadCellSx}>Статус</TableCell>
                      <TableCell sx={tableHeadCellSx}>Uplink</TableCell>
                      <TableCell sx={tableHeadCellSx}>БС</TableCell>
                      <TableCell sx={tableHeadCellSx}>RSSI/SNR</TableCell>
                      <TableCell sx={tableHeadCellSx}>Причины</TableCell>
                      <TableCell sx={tableHeadCellSx}>Действие</TableCell>
                      <TableCell sx={tableHeadCellSx}>Последний сигнал</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDevices.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        onClick={() => {
                          if (item.apartment_id) {
                            navigate(`/apartments/${item.apartment_id}?deviceId=${item.device_id}`);
                          }
                        }}
                        sx={{ cursor: item.apartment_id ? 'pointer' : 'default' }}
                      >
                        <TableCell sx={tableCellSx}>
                          <Typography sx={{ fontSize: 13, color: '#1F3550', fontWeight: 850 }}>
                            {item.device_serial_number || item.device_dev_eui || item.device_id}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748B' }}>
                            {item.device_system_type || 'LoRaWAN'} {item.device_dev_eui ? `· ${item.device_dev_eui}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableCellSx}><HealthChip value={item.health_status} /></TableCell>
                        <TableCell sx={tableCellSx}>{item.uplinks_count}</TableCell>
                        <TableCell sx={tableCellSx}>{formatNumber(item.gateway_count_avg)}</TableCell>
                        <TableCell sx={tableCellSx}>
                          {formatNumber(item.rssi_min, ' dBm')} / {formatNumber(item.snr_min, ' dB')}
                        </TableCell>
                        <TableCell sx={tableCellSx}><ReasonChips reasons={item.reasons} /></TableCell>
                        <TableCell sx={tableCellSx}><ActionHint reasons={item.reasons} /></TableCell>
                        <TableCell sx={tableCellSx}>{formatDate(item.last_seen_at)}</TableCell>
                      </TableRow>
                    ))}
                    {!filteredProblemDevices.length ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ ...tableCellSx, py: 4, textAlign: 'center', color: '#64748B' }}>
                          Нет данных по выбранному фильтру.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredProblemDevices.length > DEVICE_PAGE_SIZE ? (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ px: 1.4, py: 1, borderTop: '1px solid rgba(15, 23, 42, 0.06)' }}
                >
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                    Показано {deviceRangeStart}-{deviceRangeEnd} из {filteredProblemDevices.length}
                  </Typography>
                  <Pagination
                    size="small"
                    count={devicePageCount}
                    page={safeDevicePage}
                    onChange={(_, page) => setDevicePage(page)}
                    siblingCount={1}
                    boundaryCount={1}
                    sx={{
                      '& .MuiPagination-ul': { justifyContent: { xs: 'center', sm: 'flex-end' } },
                      '& .MuiPaginationItem-root': { fontWeight: 800 },
                    }}
                  />
                </Stack>
              ) : null}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.4, py: 1.1 }}>
                <Typography variant="subtitle1" sx={{ color: '#1F3550', fontWeight: 900 }}>
                  {gatewayListTitle}
                </Typography>
                <Chip
                  size="small"
                  label={
                    gatewayFilter === 'all'
                      ? `${filteredGateways.length || 0} из ${overview?.gateways_total || 0}`
                      : `${filteredGateways.length || 0}`
                  }
                  variant="outlined"
                />
              </Stack>
              <Divider />
              <TableContainer sx={{ maxHeight: 460 }}>
                <Table size="small" stickyHeader sx={{ minWidth: 860 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>БС</TableCell>
                      <TableCell sx={tableHeadCellSx}>Привязка</TableCell>
                      <TableCell sx={tableHeadCellSx}>Статус</TableCell>
                      <TableCell sx={tableHeadCellSx}>Пакеты</TableCell>
                      <TableCell sx={tableHeadCellSx}>Устройства</TableCell>
                      <TableCell sx={tableHeadCellSx}>RSSI/SNR</TableCell>
                      <TableCell sx={tableHeadCellSx}>Действие</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedGateways.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        onClick={() => openGatewayDetail(item)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={tableCellSx}>
                          <Typography sx={{ fontSize: 13, color: '#1F3550', fontWeight: 850 }}>
                            {item.gateway_name || item.gateway_id}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748B' }}>
                            {item.gateway_id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableCellSx}><GatewayBindingChip gateway={item} /></TableCell>
                        <TableCell sx={tableCellSx}><HealthChip value={item.health_status} /></TableCell>
                        <TableCell sx={tableCellSx}>
                          <Typography sx={{ fontSize: 13, fontWeight: 850 }}>{item.uplinks_count}</Typography>
                          {item.statistics_source === 'live' ? (
                            <Typography variant="caption" sx={{ color: '#64748B', whiteSpace: 'nowrap' }}>
                              {item.known_packets} распознано · {item.unknown_packets} неизвестно
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#94A3B8' }}>Последний расчёт</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={tableCellSx}>{item.unique_devices_count}</TableCell>
                        <TableCell sx={tableCellSx}>
                          {formatNumber(item.rssi_min, ' dBm')} / {formatNumber(item.snr_min, ' dB')}
                        </TableCell>
                        <TableCell sx={tableCellSx}>
                          {item.configuration_status === 'linked' ? (
                            <ActionHint reasons={item.reasons} />
                          ) : (
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<SettingsRoundedIcon fontSize="small" />}
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate('/gateways');
                              }}
                              sx={{ textTransform: 'none', fontWeight: 850, whiteSpace: 'nowrap' }}
                            >
                              Настроить
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!filteredGateways.length ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ ...tableCellSx, py: 4, textAlign: 'center', color: '#64748B' }}>
                          Нет данных по выбранному фильтру.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredGateways.length > GATEWAY_PAGE_SIZE ? (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ px: 1.4, py: 1, borderTop: '1px solid rgba(15, 23, 42, 0.06)' }}
                >
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                    Показано {gatewayRangeStart}-{gatewayRangeEnd} из {filteredGateways.length}
                  </Typography>
                  <Pagination
                    size="small"
                    count={gatewayPageCount}
                    page={safeGatewayPage}
                    onChange={(_, page) => setGatewayPage(page)}
                    siblingCount={1}
                    boundaryCount={1}
                    sx={{
                      '& .MuiPagination-ul': { justifyContent: { xs: 'center', sm: 'flex-end' } },
                      '& .MuiPaginationItem-root': { fontWeight: 800 },
                    }}
                  />
                </Stack>
              ) : null}
            </Paper>
          </Grid>
        </Grid>
      </Stack>
      <Dialog
        open={Boolean(selectedGateway)}
        onClose={() => setSelectedGateway(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CellTowerRoundedIcon sx={{ color: '#2E73B8' }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#1F3550', fontWeight: 900 }}>
                {selectedGateway?.gateway_name || selectedGateway?.gateway_id || 'Базовая станция'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', fontFamily: 'monospace' }}>
                {selectedGateway?.gateway_id}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ background: '#F8FAFC' }}>
          {gatewayDetailLoading ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack spacing={1.25}>
              <Grid container spacing={1}>
                <Grid item xs={6} md={3}>
                  <StatCard icon={<SensorsRoundedIcon fontSize="small" />} label="Устройства" value={gatewayDetail?.gateway?.unique_devices_count || 0} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard icon={<CloudUploadRoundedIcon fontSize="small" />} label="Uplink" value={gatewayDetail?.gateway?.uplinks_count || 0} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard icon={<CloudDownloadRoundedIcon fontSize="small" />} label="Команды" value={gatewayDetail?.gateway?.downlink_count || 0} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard icon={<TaskAltRoundedIcon fontSize="small" />} label="Ack" value={gatewayDetail?.gateway?.ack_count || 0} />
                </Grid>
              </Grid>

              <Grid container spacing={1.25}>
                <Grid item xs={12} md={7}>
                  <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
                    <Box sx={{ px: 1.4, py: 1.1 }}>
                      <Typography variant="subtitle1" sx={{ color: '#1F3550', fontWeight: 900 }}>
                        Последние uplink
                      </Typography>
                    </Box>
                    <Divider />
                    <TableContainer sx={{ maxHeight: 360 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={tableHeadCellSx}>Время</TableCell>
                            <TableCell sx={tableHeadCellSx}>Тип</TableCell>
                            <TableCell sx={tableHeadCellSx}>Прибор</TableCell>
                            <TableCell sx={tableHeadCellSx}>fCnt</TableCell>
                            <TableCell sx={tableHeadCellSx}>RSSI/SNR</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(gatewayDetail?.recent_uplinks || []).map((item) => (
                            <TableRow
                              key={item.id}
                              hover
                              onClick={() => {
                                if (item.apartment_id) {
                                  navigate(`/apartments/${item.apartment_id}?deviceId=${item.device_id}`);
                                }
                              }}
                              sx={{ cursor: item.apartment_id ? 'pointer' : 'default' }}
                            >
                              <TableCell sx={tableCellSx}>{formatDate(item.received_at)}</TableCell>
                              <TableCell sx={tableCellSx}>
                                <Chip size="small" label={item.event_type || 'event/up'} variant="outlined" />
                              </TableCell>
                              <TableCell sx={tableCellSx}>
                                <Typography sx={{ fontSize: 13, color: '#1F3550', fontWeight: 850 }}>
                                  {item.device_serial_number || item.dev_eui}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748B' }}>
                                  {item.device_system_type || 'LoRaWAN'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tableCellSx}>{item.f_cnt ?? 'Нет данных'}</TableCell>
                              <TableCell sx={tableCellSx}>
                                {formatNumber(item.rssi, ' dBm')} / {formatNumber(item.snr, ' dB')}
                              </TableCell>
                            </TableRow>
                          ))}
                          {!gatewayDetail?.recent_uplinks?.length ? (
                            <TableRow>
                              <TableCell colSpan={5} sx={{ ...tableCellSx, py: 3, textAlign: 'center', color: '#64748B' }}>
                                Uplink событий пока нет.
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
                    <Box sx={{ px: 1.4, py: 1.1 }}>
                      <Typography variant="subtitle1" sx={{ color: '#1F3550', fontWeight: 900 }}>
                        Команды и подтверждения
                      </Typography>
                    </Box>
                    <Divider />
                    <TableContainer sx={{ maxHeight: 360 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={tableHeadCellSx}>Время</TableCell>
                            <TableCell sx={tableHeadCellSx}>Тип</TableCell>
                            <TableCell sx={tableHeadCellSx}>Статус</TableCell>
                            <TableCell sx={tableHeadCellSx}>Маршрут</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(gatewayDetail?.recent_downlinks || []).map((item) => (
                            <TableRow key={item.id} hover>
                              <TableCell sx={tableCellSx}>{formatDate(item.received_at)}</TableCell>
                              <TableCell sx={tableCellSx}>
                                <Chip size="small" label={item.event_type} variant="outlined" />
                              </TableCell>
                              <TableCell sx={tableCellSx}>
                                {item.acked == null ? (
                                  <Chip size="small" label="получено" variant="outlined" />
                                ) : (
                                  <Chip
                                    size="small"
                                    label={item.acked ? 'ack' : 'ошибка'}
                                    color={item.acked ? 'success' : 'warning'}
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                              <TableCell sx={tableCellSx}>
                                {item.gateway_choice_problem ? (
                                  <Chip size="small" color="warning" label="слабая БС" variant="outlined" />
                                ) : item.best_gateway_id ? (
                                  <Chip size="small" color="success" label="норма" variant="outlined" />
                                ) : (
                                  <Typography variant="caption" sx={{ color: '#94A3B8' }}>Нет данных</Typography>
                                )}
                                {item.gateway_choice_problem ? (
                                  <Typography variant="caption" sx={{ display: 'block', color: '#64748B', mt: 0.25 }}>
                                    {formatNumber(item.selected_gateway_rssi, ' dBm')} / лучше {formatNumber(item.best_gateway_rssi, ' dBm')}
                                  </Typography>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          ))}
                          {!gatewayDetail?.recent_downlinks?.length ? (
                            <TableRow>
                              <TableCell colSpan={4} sx={{ ...tableCellSx, py: 3, textAlign: 'center', color: '#64748B' }}>
                                Событий передачи команд нет.
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {selectedGateway?.configuration_status !== 'linked' ? (
            <Button
              startIcon={<SettingsRoundedIcon />}
              onClick={() => navigate('/gateways')}
            >
              Настроить БС
            </Button>
          ) : null}
          <Button onClick={() => setSelectedGateway(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
